<script setup>
import { ref, computed } from "vue";
import WrPricingCardTick from "../atoms/icons/WrPricingCardTick.vue";

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  searchTerm: {
    type: String,
    default: "",
  },
});

const showAll = ref(false);
const itemsLimit = 5;

const displayedItems = computed(() => {
  if (!props.card.items) return [];
  return showAll.value
    ? props.card.items
    : props.card.items.slice(0, itemsLimit);
});

const hasMoreItems = computed(() => {
  return props.card.items && props.card.items.length > itemsLimit;
});

const totalItems = computed(() => {
  return props.card.items ? props.card.items.length : 0;
});

const toggleShowAll = () => {
  showAll.value = !showAll.value;
};

const highlightMatches = (text, searchTerm) => {
  if (!searchTerm.trim()) return text;
  const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedSearch})`, "gi");
  return text.replace(regex, '<span class="search-highlight">$1</span>');
};
</script>

<template>
  <div
    class="h-full flex flex-col p-5 overflow-hidden bg-white dark:bg-transparent rounded-2xl dark:[box-shadow:0_0_.15em_.1em_rgba(63,22,104,0.5)] dark:hover:[box-shadow:0_0_.15em_.1em_rgba(98,69,116,0.7)] transition-all duration-200 hover:[box-shadow:0_.0625rem_.75rem_0_rgba(38,57,82,.2)] [box-shadow:0_.3em_.3em_-.3em_hsla(0,0%,4%,.1),_0_0_0_.1rem_hsla(0,0%,4%,.08)]"
  >
    <!-- Image -->
    <NuxtLink v-if="card.headingLink" :to="card.headingLink" class="block">
      <NuxtImg
        :src="card.img"
        width="96"
        height="96"
        :alt="card.alt || 'Card image'"
        loading="lazy"
        format="webp"
      />
    </NuxtLink>
    <NuxtImg
      v-else
      :src="card.img"
      width="96"
      height="96"
      :alt="card.alt || 'card image'"
      loading="lazy"
      format="webp"
    />

    <!-- Content -->
    <div class="mt-4 flex flex-col grow">
      <!-- Heading with Total Items Count -->
      <div class="flex items-start justify-between gap-2 mb-3">
        <h3 class="text-xl font-semibold min-h-[56px]">
          <NuxtLink
            v-if="card.headingLink"
            :to="card.headingLink"
            class="hover:text-primary transition-colors"
          >
            <p v-html="highlightMatches(card.heading, searchTerm)"></p>
          </NuxtLink>
          <p v-else v-html="highlightMatches(card.heading, searchTerm)" />
        </h3>
        <span
          v-if="totalItems > 0"
          class="text-xs px-2 py-1 rounded-full font-medium bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 text-[var(--primary)]"
        >
          {{ totalItems }}
        </span>
      </div>

      <!-- Items List -->
      <ul class="space-y-4 mb-4 max-h-[300px] overflow-y-auto pl-2">
        <li v-for="(item, index) in displayedItems" :key="index">
          <NuxtLink
            :to="item.link"
            class="flex items-start gap-2 font-medium text-sm hover:text-primary transition-colors"
          >
            <WrPricingCardTick />
            <p v-html="highlightMatches(item.name, searchTerm)"></p>
          </NuxtLink>
        </li>
      </ul>

      <!-- View More/Less Button -->
      <div v-if="hasMoreItems" class="mb-4 mt-auto">
        <button
          @click="toggleShowAll"
          class="text-sm text-primary hover:text-primary/80 font-medium transition-colors duration-200 flex items-center gap-1"
        >
          <span v-if="!showAll">
            View More ({{ card.items.length - itemsLimit }} more)
          </span>
          <span v-else>View Less</span>

          <!-- Chevron Icon -->
          <svg
            :class="{ 'rotate-180': showAll }"
            class="w-4 h-4 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      <!-- Categories -->
      <!-- <div class="flex flex-wrap gap-2 mt-4">
        <span
          v-for="(cat, index) in card.category"
          :key="index"
          class="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full"
        >
          {{ cat }}
        </span>
      </div> -->
    </div>
  </div>
</template>

<style scoped>
.search-highlight {
  @apply bg-yellow-200 dark:bg-yellow-800 px-1 rounded;
}
</style>
